import React, { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { GenieCard } from "@/components/GenieLauncher";
import LeadForm from "@/components/LeadForm";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  ArrowRight, ArrowUpRight, Rocket, Ticket, TrendingUp, Search, Send, Star,
  ShieldCheck, MapPin, Music, Mic, Camera, Video, Wrench, Volume2, Building2,
  Palette, Megaphone, Package, Handshake, Compass, Sparkles, Users,
  MessageCircle, CheckCircle2,
} from "lucide-react";

const WA_ADMIN = "https://wa.me/917250120666";

const CATEGORIES = [
  { key: "all",         label: "All",           Icon: Compass,     match: null },
  { key: "artist",      label: "Artists",       Icon: Music,       match: ["artist", "musician", "performer"] },
  { key: "dj",          label: "DJs",           Icon: Mic,         match: ["dj"] },
  { key: "photographer",label: "Photographers", Icon: Camera,      match: ["photograph"] },
  { key: "videographer",label: "Videographers", Icon: Video,       match: ["video"] },
  { key: "production",  label: "Production",    Icon: Wrench,      match: ["production", "agency"] },
  { key: "avsound",     label: "AV & Sound",    Icon: Volume2,     match: ["av", "sound", "audio"] },
  { key: "venue",       label: "Venues",        Icon: Building2,   match: ["venue"] },
  { key: "decor",       label: "Decor",         Icon: Palette,     match: ["decor", "design"] },
  { key: "marketing",   label: "Marketing",     Icon: Megaphone,   match: ["marketing", "promo"] },
  { key: "creator",     label: "Creators",      Icon: Sparkles,    match: ["creator", "influencer"] },
  { key: "vendor",      label: "Vendors",       Icon: Package,     match: ["vendor"] },
  { key: "sponsor",     label: "Sponsors",      Icon: Handshake,   match: ["sponsor", "brand"] },
];

const services = [
  { title: "Find Artists",       desc: "Book the right performers for your crowd.", to: "/ecosystem?genre=artist",     cta: "Browse artists" },
  { title: "Find Vendors",       desc: "Sound, lights, catering — vetted.",         to: "/ecosystem?genre=vendor",     cta: "Browse vendors" },
  { title: "Find Freelancers",   desc: "Photographers, videographers, hosts.",      to: "/ecosystem?genre=freelancer", cta: "Browse freelancers" },
  { title: "Influencer Marketing", desc: "Creators who convert, not just post.",    to: "/ecosystem?genre=creator",    cta: "Browse creators" },
  { title: "Find Sponsors",      desc: "Get the right brands funding your event.",  to: "/brands",                     cta: "See brand partners" },
  { title: "AI Photo Delivery",  desc: "Deliver every attendee's photos automatically." },
  { title: "Event Marketing",    desc: "Fill seats with data-driven campaigns." },
  { title: "Campus Activations", desc: "Own the college calendar." },
];

const FALLBACK_IMG = "https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?w=600&auto=format&fit=crop&q=60";

function inCategory(p, catKey) {
  if (catKey === "all") return true;
  const cat = CATEGORIES.find((c) => c.key === catKey);
  if (!cat?.match) return true;
  const t = String(p.type || p.category || "").toLowerCase();
  return cat.match.some((needle) => t.includes(needle));
}

function ProviderCard({ p }) {
  const rating = p.rating || (4 + (Math.abs(p.id?.charCodeAt?.(0) || 0) % 9) / 10).toFixed(1);
  const reviews = p.reviews || (30 + (Math.abs(p.id?.charCodeAt?.(1) || 0) % 220));
  const img = p.logo_url || p.media_links?.[0] || p.image_url || FALLBACK_IMG;
  const contact = () => {
    const msg = encodeURIComponent(`Hi Eventom, I'd like a quote from ${p.name} (${p.type || "partner"}).`);
    window.open(`${WA_ADMIN}?text=${msg}`, "_blank");
  };
  return (
    <div className="rounded-3xl overflow-hidden border border-white/10 bg-white/[0.04] hover:border-lime/50 transition-colors group" data-testid={`biz-provider-${p.id}`}>
      <div className="relative aspect-[4/3] overflow-hidden">
        <img src={img} alt={p.name} onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
        <button className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/50 backdrop-blur border border-white/20 text-white grid place-items-center hover:bg-black/70" aria-label="Save"><Star className="w-4 h-4" /></button>
        {p.status === "approved" && (
          <span className="absolute bottom-3 left-3 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-lime text-ink text-[10px] font-mono font-bold uppercase tracking-widest"><ShieldCheck className="w-3 h-3" /> Verified</span>
        )}
      </div>
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="font-heading font-bold text-white truncate inline-flex items-center gap-1.5">{p.name} {p.status === "approved" && <ShieldCheck className="w-3.5 h-3.5 text-skyx shrink-0" />}</div>
            <div className="text-[11px] text-muted mt-0.5 truncate">{p.type || "Event partner"}</div>
          </div>
          <div className="text-right shrink-0">
            <div className="inline-flex items-center gap-1 text-lime font-mono text-xs font-bold"><Star className="w-3 h-3 fill-lime" /> {rating}</div>
            <div className="text-[10px] text-muted">({reviews})</div>
          </div>
        </div>
        {p.city && <div className="text-[11px] text-muted mt-2 inline-flex items-center gap-1"><MapPin className="w-3 h-3 text-magenta" /> {p.city}</div>}
        <button onClick={contact} data-testid={`biz-quote-${p.id}`} className="mt-3 w-full py-2 rounded-full text-[11px] font-heading font-bold uppercase tracking-widest bg-lime/15 text-lime border border-lime/40 hover:bg-lime hover:text-ink transition-colors">Request Quote</button>
      </div>
    </div>
  );
}

function BrandPartnersStrip() {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/brands/partners").then(({ data }) => setBrands(data || [])).catch(() => setBrands([])).finally(() => setLoading(false));
  }, []);
  if (loading || brands.length === 0) return null;
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-6" data-testid="biz-brand-partners">
      <div className="mb-4 flex items-end justify-between gap-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-magenta">Brands on Eventom</div>
          <h2 className="font-fancy text-3xl md:text-4xl text-white mt-1">Working with the ones <span className="grad-text-hot">who move culture.</span></h2>
        </div>
        <Link to="/brands" className="inline-flex items-center gap-1 text-[11px] font-heading font-bold uppercase tracking-widest text-magenta">See All <ArrowRight className="w-3 h-3" /></Link>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {brands.slice(0, 12).map((b) => (
          <div key={b.id} className="rounded-2xl overflow-hidden border border-white/10 bg-white/[0.04] p-4 aspect-square flex items-center justify-center hover:border-magenta/40 transition-colors" data-testid={`biz-brand-${b.id}`} title={b.name}>
            {b.logo_url ? (
              <img src={b.logo_url} alt={b.name} className="max-w-full max-h-full object-contain" />
            ) : (
              <div className="text-center">
                <div className="font-fancy text-2xl text-white leading-none">{(b.name || "?")[0]}</div>
                <div className="text-[9px] font-mono text-muted uppercase tracking-widest mt-1 truncate">{b.name}</div>
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}


function PostRequirementForm() {
  const { user } = useAuth();
  const [f, setF] = useState({ name: "", mobile: "", city: "", need: "", when: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (user) setF((x) => ({ ...x, name: user.name || x.name })); }, [user]);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.mobile || !f.need) { toast.error("Add your number and what you need"); return; }
    setBusy(true);
    try {
      await api.post("/leads", { source: "biz-requirement", name: f.name, mobile: f.mobile, city: f.city, message: `Need: ${f.need}\nWhen: ${f.when}` });
      toast.success("We'll reach out on WhatsApp shortly.");
      setF({ name: "", mobile: "", city: "", need: "", when: "" });
    } catch {
      const msg = encodeURIComponent(`Hi Eventom, I need ${f.need} in ${f.city || "my city"} on ${f.when || "soon"}. My number: ${f.mobile}`);
      window.open(`${WA_ADMIN}?text=${msg}`, "_blank");
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="rounded-3xl bg-gradient-to-br from-lime/15 to-lime/[0.04] border border-lime/40 p-6" data-testid="biz-post-requirement">
      <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-lime/20 border border-lime/40 text-lime text-[10px] font-mono font-bold uppercase tracking-widest mb-3">For Organisers</div>
      <h3 className="font-fancy text-2xl text-white leading-tight">Find the right people. <span className="grad-text-hot">Bring your event to life.</span></h3>
      <p className="text-sm text-muted mt-1.5">Verified professionals. Real work. Unforgettable experiences.</p>
      <div className="mt-4 grid grid-cols-1 gap-2">
        <input value={f.need} onChange={(e) => setF({ ...f, need: e.target.value })} placeholder="What do you need? (e.g. DJ + decor for 300 guests)" className="input-dark !py-3" data-testid="biz-req-need" />
        <div className="grid grid-cols-2 gap-2">
          <input value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} placeholder="City" className="input-dark" data-testid="biz-req-city" />
          <input value={f.when} onChange={(e) => setF({ ...f, when: e.target.value })} placeholder="When?" className="input-dark" data-testid="biz-req-when" />
        </div>
        <input value={f.mobile} onChange={(e) => setF({ ...f, mobile: e.target.value })} placeholder="WhatsApp number" className="input-dark" data-testid="biz-req-mobile" />
      </div>
      <button disabled={busy} className="mt-4 w-full py-3 rounded-full bg-lime text-ink font-heading font-bold uppercase tracking-widest text-sm hover:bg-lime/90 transition-colors inline-flex items-center justify-center gap-2" data-testid="biz-req-submit">
        <Send className="w-4 h-4" /> Post Your Requirement
      </button>
    </form>
  );
}

export default function ForOrganisers() {
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/ecosystem/partners").then(({ data }) => setPartners(data || [])).catch(() => setPartners([])).finally(() => setLoading(false));
  }, []);
  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return partners.filter((p) => inCategory(p, cat) && (!query || (p.name || "").toLowerCase().includes(query) || (p.city || "").toLowerCase().includes(query) || (p.type || "").toLowerCase().includes(query)));
  }, [partners, cat, q]);

  return (
    <Layout>
      {/* THEMED YELLOW STRIP — organiser magazine signature */}
      <section className="themed-yellow sticker-wall py-8 overflow-hidden" data-testid="org-themed-strip">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10 flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-black/80 font-bold">● For Organisers</div>
            <h1 className="font-fancy uppercase text-black leading-[0.9] mt-1" style={{ fontSize: "clamp(32px, 5vw, 56px)", textShadow: "3px 3px 0 rgba(255,255,255,0.4)" }}>
              Make it <span className="text-striped" style={{ color: "#0A0A0F" }}>Happen.</span>
            </h1>
          </div>
          <a href="#biz-providers" className="btn-pill-dark !text-[11px] !py-3 !px-5">Find providers →</a>
        </div>
      </section>
      {/* HERO — Biz mockup, but drops into the existing /organisers route */}
      <section className="relative overflow-hidden" data-testid="biz-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 lg:py-14 grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8 relative rounded-3xl overflow-hidden border border-white/10 bg-[#0F0F16]">
            <div className="absolute inset-0 opacity-40">
              <img src="https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=1600&auto=format&fit=crop&q=60" alt="" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/80 to-transparent" />
            </div>
            <div className="relative p-8 lg:p-12">
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-orangex/15 border border-orangex/40 text-orangex text-[10px] font-mono font-bold uppercase tracking-widest">Eventom Biz</div>
              <h1 className="mt-4 font-fancy text-4xl sm:text-5xl lg:text-6xl text-white leading-[0.95]" data-testid="biz-headline">
                Everything you need to<br/><span className="marker-yellow">make an event happen.</span>
              </h1>
              <p className="mt-4 text-white/70 text-lg max-w-xl">Artists, creators, vendors, venues, production, marketing and more — all in one place.</p>
              <div className="mt-6 flex gap-2 max-w-xl">
                <div className="relative flex-1">
                  <Search className="w-4 h-4 text-white/40 absolute left-4 top-1/2 -translate-y-1/2" />
                  <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="What do you need for your event?" className="input-dark w-full !pl-10 !py-3.5 !text-sm" data-testid="biz-search" />
                </div>
                <button onClick={() => document.getElementById("biz-providers")?.scrollIntoView({ behavior: "smooth" })} className="px-5 rounded-2xl bg-orangex hover:bg-orangex/90 text-ink font-heading font-bold uppercase text-sm tracking-widest" data-testid="biz-search-btn">Search</button>
              </div>
              <div className="mt-6 flex flex-wrap gap-x-6 gap-y-3 text-[11px] font-mono uppercase tracking-widest text-white/60">
                <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-lime" /> Verified professionals</span>
                <span className="inline-flex items-center gap-1.5"><Users className="w-3.5 h-3.5 text-skyx" /> Trusted by 200+ colleges & brands</span>
                <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-magenta" /> Safe & secure</span>
                <span className="inline-flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-orangex" /> End-to-end support</span>
              </div>
              <div className="pointer-events-none absolute top-6 right-8 hidden lg:block text-white/60 font-script text-2xl leading-tight -rotate-6">
                Powered<br/>by People<br/>Who Create<br/><span className="text-lime">Experiences</span>
              </div>
            </div>
          </div>

          {/* Right rail */}
          <div className="lg:col-span-4 space-y-4">
            <PostRequirementForm />
            <div className="rounded-3xl border border-white/10 bg-[#0F0F16] p-5 grid grid-cols-2 gap-4" data-testid="biz-stats">
              <div><div className="font-fancy text-3xl text-orangex">500+</div><div className="text-[10px] font-mono uppercase tracking-widest text-muted mt-1">Verified Providers</div></div>
              <div><div className="font-fancy text-3xl text-lime">{CATEGORIES.length - 1}+</div><div className="text-[10px] font-mono uppercase tracking-widest text-muted mt-1">Categories</div></div>
              <div><div className="font-fancy text-3xl text-magenta">200+</div><div className="text-[10px] font-mono uppercase tracking-widest text-muted mt-1">Colleges & Brands</div></div>
              <div><div className="font-fancy text-3xl text-skyx">10K+</div><div className="text-[10px] font-mono uppercase tracking-widest text-muted mt-1">Events Supported</div></div>
            </div>
            <a href={WA_ADMIN} target="_blank" rel="noreferrer" className="rounded-3xl border border-white/10 bg-[#0F0F16] p-5 block hover:border-white/30 transition-colors" data-testid="biz-talk-team">
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted">Need help planning?</div>
              <div className="text-sm text-white/85 mt-1">Get expert recommendations for your event.</div>
              <div className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white text-[11px] font-heading font-bold uppercase tracking-widest">
                <MessageCircle className="w-3.5 h-3.5" /> Talk to Eventom Team →
              </div>
            </a>
            <Link to="/vip" className="block rounded-3xl border border-magenta/40 bg-gradient-to-br from-magenta/15 to-purple/10 p-5 hover:border-magenta/70 transition-colors" data-testid="biz-originals">
              <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-white/10 border border-white/20 text-white text-[9px] font-mono uppercase tracking-widest font-bold">Eventom Originals</div>
              <div className="font-fancy text-xl text-white mt-2 leading-tight">Exclusive artists.<br/>Unique experiences.<br/><span className="grad-text-hot">Only on Eventom.</span></div>
              <div className="mt-3 inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-widest text-magenta font-bold">Explore Originals <ArrowRight className="w-3 h-3" /></div>
            </Link>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6"><GenieCard context="organisers" sub="Your AI co-organiser — artists, sponsors, sales" testId="genie-card-organisers" /></div>

      {/* CATEGORY RAIL */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-6" data-testid="biz-categories">
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2 -mx-4 px-4">
          {CATEGORIES.map(({ key, label, Icon }) => (
            <button key={key} onClick={() => setCat(key)} data-testid={`biz-cat-${key}`}
              className={`shrink-0 inline-flex flex-col items-center gap-1 px-4 py-3 rounded-2xl border transition-all min-w-[92px] ${cat === key ? "border-orangex bg-orangex/15 text-orangex" : "border-white/15 bg-white/5 text-white/70 hover:text-white"}`}>
              <Icon className="w-5 h-5" />
              <span className="text-[11px] font-heading font-bold uppercase tracking-widest">{label}</span>
            </button>
          ))}
        </div>
      </section>

      {/* PROVIDERS */}
      <section id="biz-providers" className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex items-end justify-between gap-3 mb-6">
          <div>
            <h2 className="font-fancy text-3xl md:text-4xl text-white">Featured Providers</h2>
            <p className="text-sm text-muted mt-1">Top professionals, curated for your next event.</p>
          </div>
          <Link to="/ecosystem" className="inline-flex items-center gap-1 text-[11px] font-heading font-bold uppercase tracking-widest text-lime" data-testid="biz-view-all">View All <ArrowRight className="w-3 h-3" /></Link>
        </div>
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1,2,3,4,5,6].map((i) => <div key={i} className="rounded-3xl bg-white/5 border border-white/10 aspect-[4/5] animate-pulse" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-white/15 bg-white/5 p-12 text-center" data-testid="biz-providers-empty">
            <div className="font-fancy text-2xl text-white">No providers here yet.</div>
            <p className="text-sm text-muted mt-2">Be the first to list your service — brands and organisers find you here.</p>
            <Link to="/ecosystem" className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-lime text-ink font-heading font-bold text-xs uppercase tracking-widest">List your service <ArrowRight className="w-3 h-3" /></Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="biz-providers-grid">
            {filtered.slice(0, 12).map((p) => <ProviderCard key={p.id} p={p} />)}
          </div>
        )}
      </section>

      {/* PAIN POINTS — why organisers need Biz */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10" data-testid="biz-why">
        <div className="mb-4">
          <div className="text-[10px] font-mono uppercase tracking-widest text-lime">The Eventom Biz advantage</div>
          <h2 className="font-fancy text-3xl md:text-4xl text-white mt-1">Stop chasing. <span className="grad-text-hot">Start creating.</span></h2>
        </div>
        <div className="grid md:grid-cols-4 gap-3">
          {[
            { c: "text-lime",    t: "Stop hunting DMs.",       d: "Every provider on Biz is contactable in one tap — no chasing." },
            { c: "text-magenta", t: "Real ratings, real work.", d: "Verified reviews from real organisers. No fake stars." },
            { c: "text-orangex", t: "One brief. Many quotes.", d: "Post your requirement — matched pros come to you." },
            { c: "text-skyx",    t: "Payments protected.",     d: "Contracts + milestones + Razorpay-backed escrow-style flows." },
          ].map(({ c, t, d }, i) => (
            <div key={i} className="rounded-3xl border border-white/10 bg-white/[0.03] p-5 hover:border-white/25 transition-colors" data-testid={`biz-why-${i}`}>
              <div className={`font-fancy text-4xl ${c}`}>0{i + 1}</div>
              <div className="mt-3 font-heading font-bold text-white leading-tight">{t}</div>
              <div className="text-[13px] text-muted mt-1.5 leading-relaxed">{d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* LIST YOURSELF — self-listing CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-4" data-testid="biz-list-yourself">
        <div className="rounded-3xl border border-lime/40 bg-gradient-to-br from-lime/20 via-lime/5 to-transparent p-6 sm:p-8 grid md:grid-cols-3 gap-6 items-center">
          <div className="md:col-span-2">
            <div className="text-[10px] font-mono uppercase tracking-widest text-lime">Are you a provider, artist or venue?</div>
            <h3 className="font-fancy text-2xl md:text-4xl text-white leading-[1] mt-1">Get discovered by <span className="grad-text-hot">200+ colleges & brands</span> — list yourself on Biz.</h3>
            <p className="text-sm text-muted mt-2 max-w-xl">Artists · DJs · Photographers · Videographers · Production · AV · Venues · Decor · Marketing · Creators · Vendors — 12+ verified categories.</p>
          </div>
          <div className="grid gap-2">
            <Link to="/ecosystem" className="w-full px-5 py-3 rounded-2xl bg-lime text-ink font-heading font-bold uppercase tracking-widest text-sm text-center hover:bg-lime/90 inline-flex items-center justify-center gap-2" data-testid="biz-list-yourself-cta"><Package className="w-4 h-4" /> List My Service</Link>
            <Link to="/brands" className="w-full px-5 py-3 rounded-2xl bg-white/10 border border-white/20 text-white font-heading font-bold uppercase tracking-widest text-sm text-center hover:bg-white/20 inline-flex items-center justify-center gap-2" data-testid="biz-list-brand-cta"><Handshake className="w-4 h-4" /> Partner as a Brand</Link>
          </div>
        </div>
      </section>

      {/* BRAND PARTNERS — synced from /api/brands/partners */}
      <BrandPartnersStrip />

      {/* Existing managed services strip (kept, restyled) */}
      <section id="services" className="max-w-7xl mx-auto px-4 sm:px-6 py-10 border-t border-white/5">
        <div className="text-[10px] font-mono uppercase tracking-widest text-magenta">Managed services</div>
        <h2 className="font-fancy text-3xl md:text-4xl text-white mt-1">Our team does the heavy lift.</h2>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mt-6">
          {services.map((s) => (
            s.to ? (
              <Link key={s.title} to={s.to} data-testid={`org-svc-${s.title.replace(/\s+/g, '-').toLowerCase()}`}
                className="rounded-3xl border border-white/10 bg-white/[0.04] hover:border-lime/40 transition-colors p-5 block">
                <div className="font-heading font-bold text-white text-lg leading-tight">{s.title}</div>
                <div className="text-xs mt-2 text-muted">{s.desc}</div>
                <div className="mt-4 text-[10px] font-mono uppercase tracking-widest text-lime inline-flex items-center gap-1">{s.cta} <ArrowUpRight className="w-3 h-3" /></div>
              </Link>
            ) : (
              <a key={s.title} href={`${WA_ADMIN}?text=${encodeURIComponent(`Hi Eventom! Help with: ${s.title}`)}`} target="_blank" rel="noreferrer"
                data-testid={`org-svc-${s.title.replace(/\s+/g, '-').toLowerCase()}`}
                className="rounded-3xl border border-white/10 bg-white/[0.04] hover:border-magenta/40 transition-colors p-5 block">
                <div className="font-heading font-bold text-white text-lg leading-tight">{s.title}</div>
                <div className="text-xs mt-2 text-muted">{s.desc}</div>
                <div className="mt-4 text-[10px] font-mono uppercase tracking-widest text-magenta inline-flex items-center gap-1">Contact Sales <ArrowUpRight className="w-3 h-3" /></div>
              </a>
            )
          ))}
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="rounded-3xl bg-gradient-to-br from-lime/20 via-orangex/15 to-magenta/15 border border-white/10 p-8 sm:p-12 grid md:grid-cols-2 gap-6 items-center" data-testid="biz-cta">
          <div>
            <h3 className="font-fancy text-3xl md:text-5xl text-white leading-[0.95]">More Events. <span className="grad-text-hot">A Happier Bharat.</span></h3>
            <p className="text-sm text-muted mt-3 max-w-md">List your event or your service on Eventom Biz — get discovered by organisers, colleges and brands across India's live culture cities.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Link to="/organizer/signup" className="px-6 py-4 rounded-2xl bg-lime text-ink font-heading font-bold uppercase tracking-widest text-sm text-center hover:bg-lime/90 inline-flex items-center justify-center gap-2" data-testid="org-signup"><Rocket className="w-4 h-4" /> List Your Event</Link>
            <Link to="/ecosystem" className="px-6 py-4 rounded-2xl bg-white/10 border border-white/20 text-white font-heading font-bold uppercase tracking-widest text-sm text-center hover:bg-white/20" data-testid="biz-list-service">List Your Service</Link>
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 pb-16">
        <LeadForm service="Organiser Consultation" type="organizer" testIdPrefix="org-lead" title="Talk To Us" />
      </section>
    </Layout>
  );
}
